<section class="config-section">
    <h3 class="config-section-title">Markets</h3>

    <p class="config-counter config-note">
        Each service is assigned directly to a market &mdash; there are no RSID ranges and no
        day-of-week blocks.
    </p>

    <div class="markets-toolbar">
        <input class="search-input" type="text" placeholder="Search service or market"
               [(ngModel)]="searchTerm" name="searchTerm" (ngModelChange)="onSearchChange()" />
        <button kendoButton (click)="addMarket()"><i class="fas fa-plus toolbar-icon"></i>Add Market</button>
        <button kendoButton (click)="importMarkets()"><i class="fas fa-file-import toolbar-icon"></i>Import</button>
        <button kendoButton (click)="exportMarkets()"><i class="fas fa-file-export toolbar-icon"></i>Export</button>
        <div class="spacer"></div>
        <span class="dirty-note" *ngIf="isDirty">Unsaved changes &mdash; service counts refresh after save.</span>
        <button kendoButton [primary]="true" [disabled]="!isDirty || saving" (click)="save()">
            <span *ngIf="saving" class="k-i-loading saving-icon"></span>{{ saving ? 'Saving...' : 'Save' }}
        </button>
    </div>

    <!-- History link comes later, alongside the change-history feature itself. -->
    <div class="summary" *ngIf="unassigned">
        <span><b class="num">{{ marketCount }}</b> market{{ marketCount === 1 ? '' : 's' }}</span>
        <span class="sep" *ngIf="unassigned"></span>
        <span *ngIf="unassigned">
            <b class="num">{{ unassigned.total - unassigned.count }}</b> of {{ unassigned.total }} services assigned
        </span>
        <span class="sep" *ngIf="unassigned"></span>
        <span *ngIf="unassigned">
            <b class="num">{{ unassigned.count }}</b> {{ unassigned.name | lowercase }}
        </span>
        <span class="meta" *ngIf="lastChanged">
            Last changed by {{ lastChanged.user }} on {{ lastChanged.dttm | date:'dd/MM/yyyy' }}
        </span>
    </div>

    <div class="markets-grid-wrap" *ngIf="unassigned">
        <kendo-grid [data]="gridData" [scrollable]="'none'" [rowClass]="rowClass">
            <kendo-grid-column field="service" title="Market / Services" [headerClass]="'ra-grid-header'">
                <ng-template kendoGridCellTemplate let-dataItem>
                    <span class="pinned-label" *ngIf="dataItem.isPinnedRow">
                        Everything else &rarr; <strong>{{ dataItem.market }}</strong>
                    </span>

                    <ng-container *ngIf="dataItem.isEditRow && draft">
                        <div class="edit-panel">
                            <div class="ep-row">
                                <ng-container *ngIf="!dataItem.serviceRef.id; else editingExisting">
                                    <div class="ep-field ep-field-wide">
                                        <label>Services (RSIDs)</label>
                                        <textarea class="ep-input ep-textarea mono" [(ngModel)]="draft.service" name="service"
                                                  rows="2" placeholder="SE2W1204, SE2W1216 ... or paste a column/row from Excel"></textarea>
                                    </div>
                                </ng-container>
                                <ng-template #editingExisting>
                                    <div class="ep-field">
                                        <label>Service (RSID)</label>
                                        <input class="ep-input mono" [(ngModel)]="draft.service" name="service" />
                                    </div>
                                    <div class="ep-field">
                                        <label>Market</label>
                                        <select class="ep-input" [(ngModel)]="draft.targetMarket" name="targetMarket">
                                            <option *ngFor="let name of marketNames" [value]="name">{{ name }}</option>
                                        </select>
                                    </div>
                                </ng-template>
                                <div class="ep-actions">
                                    <button kendoButton (click)="cancelEdit(dataItem.marketRef, dataItem.serviceRef)">Cancel</button>
                                    <button kendoButton [primary]="true" (click)="commitEdit(dataItem.marketRef, dataItem.serviceRef)">Done</button>
                                </div>
                            </div>

                            <div class="ep-error" *ngIf="draftError">
                                <i class="fas fa-times-circle"></i> {{ draftError }}
                            </div>
                        </div>
                    </ng-container>

                    <a href="#" class="show-more-link" *ngIf="dataItem.isShowMoreRow"
                       (click)="showMoreServices(); $event.preventDefault()">
                        Show {{ dataItem.remainingCount }} more service{{ dataItem.remainingCount === 1 ? '' : 's' }}&hellip;
                    </a>

                    <ng-container *ngIf="!dataItem.isPinnedRow && !dataItem.isEditRow && !dataItem.isShowMoreRow">
                        <ng-container *ngIf="dataItem.isGroupRow; else serviceCell">
                            <a href="#" class="group-toggle" (click)="toggleMarket(dataItem.market); $event.preventDefault()">
                                <i class="fas fa-chevron-right caret" [class.expanded]="expandedMarkets.has(dataItem.market)"></i>
                                <span class="group-name">{{ dataItem.market }}</span>
                            </a>
                        </ng-container>
                        <ng-template #serviceCell>
                            <span class="mono" [class.retired]="!dataItem.inSchedule">{{ dataItem.service }}</span>
                            <span class="route-note" *ngIf="dataItem.inSchedule && dataItem.trainOrigin">
                                {{ dataItem.trainOrigin }} &rarr; {{ dataItem.trainDestination }}
                            </span>
                        </ng-template>
                    </ng-container>
                </ng-template>
            </kendo-grid-column>

            <kendo-grid-column field="covered" title="Services" [width]="110" [headerClass]="'ra-grid-header'">
                <ng-template kendoGridCellTemplate let-dataItem>
                    <span class="covered-count unassigned-count" *ngIf="dataItem.isPinnedRow">
                        {{ dataItem.covered }}
                    </span>
                    <span class="group-meta" *ngIf="dataItem.isGroupRow">
                        {{ dataItem.itemCount }} service{{ dataItem.itemCount === 1 ? '' : 's' }}
                    </span>
                    <ng-container *ngIf="!dataItem.isGroupRow && !dataItem.isPinnedRow && !dataItem.isEditRow && !dataItem.isShowMoreRow">
                        <span class="departures-count" *ngIf="dataItem.inSchedule">{{ departuresDisplay(dataItem) }}</span>
                        <span class="retired-note" *ngIf="!dataItem.inSchedule">not in schedule</span>
                    </ng-container>
                </ng-template>
            </kendo-grid-column>

            <kendo-grid-column field="actions" title="" [width]="110" [headerClass]="'ra-grid-header'">
                <ng-template kendoGridCellTemplate let-dataItem>
                    <span class="row-actions" *ngIf="!dataItem.isPinnedRow && !dataItem.isEditRow && !dataItem.isShowMoreRow">
                        <ng-container *ngIf="dataItem.isGroupRow; else serviceActions">
                            <i class="fas fa-plus" title="Add services" (click)="addService(dataItem.marketRef)"></i>
                            <i class="fas fa-pencil-alt" title="Rename market" (click)="renameMarket(dataItem.marketRef)"></i>
                            <i class="fas fa-trash-alt" title="Delete market" (click)="deleteMarket(dataItem.marketRef)"></i>
                        </ng-container>
                        <ng-template #serviceActions>
                            <i class="fas fa-pencil-alt" title="Edit" (click)="editService(dataItem.marketRef, dataItem.serviceRef)"></i>
                            <i class="fas fa-trash-alt" title="Remove" (click)="deleteService(dataItem.marketRef, dataItem.serviceRef)"></i>
                        </ng-template>
                    </span>
                </ng-template>
            </kendo-grid-column>
        </kendo-grid>
    </div>

    <div class="count" *ngIf="unassigned">
        {{ marketCount }} market{{ marketCount === 1 ? '' : 's' }}
        &middot; {{ serviceCount }} service{{ serviceCount === 1 ? '' : 's' }}
    </div>
</section>
